import { jsx } from 'react/jsx-runtime';
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useCookies } from 'react-cookie';
import { useI18nContext } from './I18nProvider.esm.js';
import { Cookie } from '../config/cookies.esm.js';
import { isLocationInGDPR } from '../helpers/compliance.esm.js';

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
var defaultCookieType = [
    "essential"
];
var allCookiesType = [
    "essential",
    "performance",
    "analytics"
];
var ComplianceContext = /*#__PURE__*/ React.createContext({
    isConsentBannerShown: false,
    setShowConsentBanner: function() {},
    wasCookieBannerAcceptedInteracted: null,
    cookieTypesEnabledList: defaultCookieType,
    setIsPersonalInfoView: function() {},
    isPersonalInfoView: false,
    isCCPA: false
});
var ComplianceProvider = function(param) {
    var children = param.children;
    var _cookies_Cookie_Geography_split, _cookies_Cookie_Geography;
    var _useCookies = _sliced_to_array(useCookies([
        Cookie.EnableCookie,
        Cookie.CookieTypesEnabled,
        Cookie.CookieBannerAccepted,
        Cookie.Geography
    ]), 1), cookies = _useCookies[0];
    var region = useI18nContext().region;
    var isCCPA = cookies[Cookie.Geography] === "US-CA";
    var countryIsoDerivedFromGeography = (_cookies_Cookie_Geography_split = (_cookies_Cookie_Geography = cookies[Cookie.Geography]) === null || _cookies_Cookie_Geography === void 0 ? void 0 : _cookies_Cookie_Geography.split("-")) === null || _cookies_Cookie_Geography_split === void 0 ? void 0 : _cookies_Cookie_Geography_split[0];
    var cookieTypesFromCookies = cookies[Cookie.CookieTypesEnabled];
    var isTrackingViaCookiesEnabled = cookies[Cookie.EnableCookie] === "true";
    var wasCookieBannerAcceptedInteracted = cookies[Cookie.CookieBannerAccepted] != null;
    var _useState = _sliced_to_array(useState(false), 2), isPersonalInfoView = _useState[0], setIsPersonalInfoView = _useState[1];
    var _useState1 = _sliced_to_array(useState(!wasCookieBannerAcceptedInteracted && !isTrackingViaCookiesEnabled), 2), isConsentBannerShown = _useState1[0], setShowConsentBanner = _useState1[1];
    useEffect(function() {
        // reset personal view when banner is closed
        if (!isConsentBannerShown) {
            setIsPersonalInfoView(false);
        }
    }, [
        isConsentBannerShown
    ]);
    var cookieTypesEnabledList = useMemo(function() {
        var isUserUnderGDPR = isLocationInGDPR({
            region: region,
            countryIso: countryIsoDerivedFromGeography
        });
        if (isCCPA || isUserUnderGDPR) {
            return cookieTypesFromCookies != null ? decodeURIComponent(cookieTypesFromCookies).split(",") : defaultCookieType;
        }
        return allCookiesType;
    }, [
        cookieTypesFromCookies,
        countryIsoDerivedFromGeography,
        isCCPA,
        region
    ]);
    var value = useMemo(function() {
        return {
            cookieTypesEnabledList: cookieTypesEnabledList,
            isPersonalInfoView: isPersonalInfoView,
            setIsPersonalInfoView: setIsPersonalInfoView,
            isConsentBannerShown: isConsentBannerShown,
            setShowConsentBanner: setShowConsentBanner,
            wasCookieBannerAcceptedInteracted: wasCookieBannerAcceptedInteracted,
            isCCPA: isCCPA
        };
    }, [
        wasCookieBannerAcceptedInteracted,
        cookieTypesEnabledList,
        isConsentBannerShown,
        isPersonalInfoView,
        isCCPA
    ]);
    return /*#__PURE__*/ jsx(ComplianceContext.Provider, {
        value: value,
        children: children
    });
};
var useComplianceProvider = function() {
    return useContext(ComplianceContext);
};

export { ComplianceContext, ComplianceProvider, allCookiesType, defaultCookieType, useComplianceProvider };
